/**
 * Modal Responsive Styles
 * 
 * Provides a complete responsive styles object for the WhatsNewModal component
 * using Material-UI's breakpoint system with theme integration.
 * 
 * Responsive behavior:
 * - Mobile (xs < 600px): Full-width modal with 90vw width and bottom sheet feel
 * - Tablet (sm: 600px - 960px): Centered modal with 90vw width
 * - Desktop (md: 960px+): Centered modal with fixed 600px width
 * 
 * Animations:
 * - Dialog entrance: Scale and fade-in effect (300ms)
 * - Dialog exit: Scale and fade-out effect (200ms)
 * - Backdrop: Smooth fade-in (300ms)
 * - Scrollbar: Hover effect for better visual feedback
 */

export const modalStyles = {
  dialog: {
    '& .MuiDialog-paper': {
      width: '100%',
      maxWidth: {
        xs: '90vw',
        sm: '90vw',
        md: '600px',
      },
      margin: {
        xs: '16px auto',
        sm: '32px auto',
      },
      borderRadius: {
        xs: '8px',
        sm: '12px',
      },
      overflow: 'hidden',
      boxShadow: {
        xs: '0px 5px 20px rgba(0, 0, 0, 0.3)',
        sm: '0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14)',
      },
      // Smooth transition animation
      transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
  
  dialogContent: {
    maxHeight: {
      xs: '70vh',
      sm: '60vh',
      md: '65vh',
    },
    overflow: 'auto',
    py: {
      xs: 2,
      sm: 3,
    },
    px: {
      xs: 2,
      sm: 3,
    },
    // Custom scrollbar styling with theme-aware colors
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(65, 200, 196, 0.3)', // Theme primary color with transparency
      borderRadius: '4px',
      transition: 'background-color 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: 'rgba(65, 200, 196, 0.6)', // Darker on hover
      },
    },
  },
  
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    pb: {
      xs: 1.5,
      sm: 2,
    },
    px: {
      xs: 2,
      sm: 3,
    },
    pt: {
      xs: 2,
      sm: 2.5,
    },
    borderBottom: '1px solid',
    borderColor: 'divider',
    gap: 1,
    flexWrap: 'wrap',
    backgroundColor: 'background.paper',
  },
  
  dialogActions: {
    p: 2,
    borderTop: '1px solid',
    borderColor: 'divider',
    justifyContent: 'flex-end',
    flexDirection: {
      xs: 'column-reverse',
      sm: 'row',
    },
    gap: {
      xs: 1,
      sm: 0,
    },
    backgroundColor: 'background.paper',
    '& button': {
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
      },
    },
  },
  
  // Bottom sheet style for mobile (optional, for alternative approach)
  mobileSheet: {
    '& .MuiDialog-paper': {
      position: 'fixed',
      bottom: 0,
      margin: 0,
      width: '100%',
      maxWidth: '100%',
      borderRadius: '16px 16px 0 0',
      maxHeight: '80vh',
      overflow: 'hidden',
      animation: 'slideUp 300ms cubic-bezier(0.4, 0, 0.2, 1)',
      '@keyframes slideUp': {
        from: {
          transform: 'translateY(100%)',
          opacity: 0,
        },
        to: {
          transform: 'translateY(0)',
          opacity: 1,
        },
      },
    },
  },
  
  // Transition/animation styles
  transitions: {
    dialogEnter: {
      animation: 'modalEnter 300ms cubic-bezier(0.4, 0, 0.2, 1)',
      '@keyframes modalEnter': {
        from: {
          opacity: 0,
          transform: 'scale(0.9)',
        },
        to: {
          opacity: 1,
          transform: 'scale(1)',
        },
      },
    },
    dialogExit: {
      animation: 'modalExit 200ms cubic-bezier(0.4, 0, 0.2, 1)',
      '@keyframes modalExit': {
        from: {
          opacity: 1,
          transform: 'scale(1)',
        },
        to: {
          opacity: 0,
          transform: 'scale(0.9)',
        },
      },
    },
    backdropEnter: {
      animation: 'backdropEnter 300ms ease-in-out',
      '@keyframes backdropEnter': {
        from: {
          opacity: 0,
        },
        to: {
          opacity: 1,
        },
      },
    },
  },
};

export default modalStyles;
